import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import { useShopStore } from '@/stores';
import ShopFnbFacilityCategoryButton from '@/components/common/shop-fnb-facility/buttons/category';
import { useShopCategories } from '@/hooks/poi/useShopData';
import type { BuildingCategory } from '@/services/dto';
import { useTranslation } from 'react-i18next';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import { useOrientation } from '@/hooks';
import { getCategoryLocalizedName } from '@/utils/poi';
import clsx from 'clsx';

const getCategoryLabel = (category: BuildingCategory, language: string, t: (key: string) => string): string => {
    if (category.clientCode === 'all') {
        const name = category.name;
        if (typeof name === 'string') return t(name);
        if (Array.isArray(name)) return getCategoryLocalizedName(category, language);
        return t(name[language]);
    }
    return getCategoryLocalizedName(category, language);
};

interface ShopSearchByCategoryProps {
    onSelectCategory?: (category: BuildingCategory) => void;
}

const ShopSearchByCategory = ({ onSelectCategory }: ShopSearchByCategoryProps) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const { isHorizontal } = useOrientation();
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const { t } = useTranslation();
    const selectedCategory = useShopStore((state) => state.selectedCategory);
    const setSelectedCategory = useShopStore((state) => state.setSelectedCategory);
    const updateShopContents = useShopStore((state) => state.updateShopContents);
    const shop = useShopCategories();

    const handleSelectCategory = (category: BuildingCategory) => {
        setSelectedCategory(category);
        onSelectCategory?.(category);
        updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY);
    };

    return (
        <div
            data-region-name="shop-search-by-category"
            className={clsx(styles['shop-search-by-category'], {
                [styles['shop-search-by-category--vertical']]: !isHorizontal,
                [styles['shop-search-by-category--vertical--halfmode']]: !isHorizontal && screenMode === 'split',
            })}
            data-region-nav
        >
            {shop.map((category) => (
                <ShopFnbFacilityCategoryButton
                    tabIndex={9}
                    data-interactive
                    role="radio"
                    type="button"
                    aria-checked={selectedCategory?.clientCode === category.clientCode}
                    aria-label={getCategoryLabel(category, language, t)}
                    key={category.clientCode}
                    onClick={() => handleSelectCategory(category)}
                    title={getCategoryLabel(category, language, t)}
                />
            ))}
        </div>
    );
};

export default ShopSearchByCategory;
